import { ReactNode } from 'react';
import { Box, Collapse, Stack, Typography } from '@mui/material';
import { LINE, resetButton } from './constants';

interface Props {
  title: string;
  /** Small grey pill after the title, e.g. "Web only". */
  tag?: string;
  value?: string;
  /** Colour chip shown before the value. */
  swatch?: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}

function Accordion({
  title,
  tag,
  value,
  swatch,
  open,
  onToggle,
  children,
}: Props) {
  return (
    <Box
      sx={{
        flex: 'none',
        border: '1px solid #000',
        borderRadius: '10px',
        background: '#fff',
        overflow: 'hidden',
      }}>
      <Box
        component="button"
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        sx={{
          ...resetButton,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          width: '100%',
          padding: '16px 18px',
        }}>
        <Stack direction="row" alignItems="center" gap="10px">
          <Typography variant="h4" sx={{ color: 'text.primary' }}>
            {title}
          </Typography>
          {tag && (
            <Box
              sx={{
                padding: '3px 8px',
                borderRadius: '50px',
                background: 'rgb(242, 242, 242)',
                color: 'text.secondary',
                typography: 'caption',
                fontWeight: 600,
              }}>
              {tag}
            </Box>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" gap="10px" minWidth={0}>
          {swatch && (
            <Box
              sx={{
                width: 16,
                height: 16,
                flex: 'none',
                borderRadius: '4px',
                border: '1px solid rgba(0, 0, 0, 0.15)',
                background: swatch,
              }}
            />
          )}
          {value && (
            <Typography
              variant="subtitle2"
              sx={{
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {value}
            </Typography>
          )}
          <Box
            aria-hidden
            sx={{
              color: 'text.tertiary',
              flex: 'none',
              transition: 'transform 150ms',
              transform: open ? 'rotate(90deg)' : 'none',
              typography: 'body2',
            }}>
            ›
          </Box>
        </Stack>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ borderTop: `1px solid ${LINE}`, padding: '18px' }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

export default Accordion;
